import type {BookingReservationObjTaxesFees} from "@hosttools/core/channels/booking/types";
import type {ReservationRate} from "@hosttools/core/generated/models";
import type {
    LegacyFields,
    Reservation as ReservationModel
} from "@hosttools/core/shared/model/reservation";
import {
    getCheckInTime,
    getCheckOutTime,
    getEmail,
    getExternalUrl,
    getFirstName,
    getFullName,
    getLastName,
    getPhone,
    getPreferLanguage
} from "@hosttools/core/shared/utils/utils.reservation";

import type {Listing} from "./listing";

export type ReservationRaw = MongooseModel2Client<ReservationModel> &
    Partial<LegacyFields> & {
        post?: Required<MongooseModel2Client<ReservationModel>>["posts"][number];
    };

export type ReservationPost = NonNullable<ReservationRaw["posts"]>[number] & {
    pending?: boolean;
};

export type ReservationSource = ReservationRaw["source"]; // Exclude<Channel, Channel.August | Channel.Seam>;

type ReservationStatusColors = "success" | "error" | "blue" | "primary";

export class Reservation {
    raw: ReservationRaw;

    _id: string;

    source: ReservationSource;

    accountID: string | undefined;

    firstName: string;

    lastName: string;

    fullName: string;

    initial: string;

    createdAt: string | undefined;

    airbnbThreadID?: string;

    thumbnailUrl: string | undefined;

    email: string;

    phone: string;

    preferredLocale: string;

    guests?: ReservationRaw["guests"];

    nights?: number;

    confirmationCode?: string;

    startDate: string;

    endDate: string;

    listingID: string;

    status: ReservationRaw["status"];

    checkInTime?: number;

    checkOutTime?: number;

    notes: string;

    guestPrice: number;

    hostPayout: number;

    nextScheduledMessageAt: string | undefined;

    lastMessageAt: string | undefined;

    calendarID?: string;

    posts: ReservationRaw["posts"];

    lastPost: ReservationPost | undefined;

    unread: number;

    externalUrl: string;

    statusLabel: string;

    statusClassNames: string;

    statusColor: ReservationStatusColors;

    confirmationDate?: string;

    custom?: ReservationRaw["custom"];

    guideBookURL?: string;

    rates?: ReservationRate[];

    changes?: ReservationRaw["changes"];

    constructor(data: ReservationRaw) {
        this.raw = data;
        this._id = data._id;
        this.source = data.source;
        this.firstName = getFirstName(data);
        this.lastName = getLastName(data);
        this.fullName = getFullName(data);
        this.initial = takeFirstLetter(this.fullName);
        this.email = getEmail(data);
        this.preferredLocale = getPreferLanguage(data);
        this.phone = getPhone(data);
        this.thumbnailUrl = data.airbnbThumbnailUrl;
        this.nights = data.airbnbNights ? Number(data.airbnbNights) : undefined;
        this.accountID = data.accountID;
        this.createdAt = data.createdAt;
        this.confirmationCode = getConfirmationCode(data);
        this.startDate = data.startDate;
        this.endDate = data.endDate;
        this.listingID = data.listingID;
        this.status = data.status;
        this.checkInTime = data.custom?.checkInTime;
        this.checkOutTime = data.custom?.checkOutTime;
        this.nextScheduledMessageAt = data.nextScheduledMessageAt;
        this.lastMessageAt = data.airbnbLastMessageAt;
        this.notes = data.custom?.notes ?? "";
        this.guests = data.custom?.guests ?? data.guests;
        this.calendarID = data.calendarID;
        this.guestPrice = data.guestPrice ?? 0;
        this.hostPayout = data.hostPayout ?? 0;
        this.posts = data.posts;
        this.unread = data.unread ?? 0;
        this.externalUrl = getExternalUrl(data);
        const {label, classNames, color} = reservationStatusMap(data.status);
        this.statusLabel = label;
        this.statusClassNames = classNames;
        this.statusColor = color;
        this.confirmationDate = data.confirmationDate;
        this.custom = data.custom;
        this.lastPost = data.post;
        this.guideBookURL = data.guideBookURL;
        this.rates = data.rates;
        this.changes = data.changes;
    }
}

export const getCheckInOutTime = (reservation: Reservation, listing: Listing) => {
    return {
        checkInTime: getCheckInTime(reservation, listing),
        checkOutTime: getCheckOutTime(reservation, listing)
    };
};

export function getConfirmationCode(reservation: ReservationRaw) {
    switch (reservation.source) {
        case "Airbnb":
            return reservation.airbnbConfirmationCode;

        case "Booking":
            return reservation.bookingReservationID;

        case "HomeAway":
            return reservation.homeAwayReservationID;

        case "Houfy":
            return reservation.houfyReservationID;

        default:
            return reservation?.airbnbConfirmationCode;
    }
}

export function shortGuestName({firstName, lastName}: Reservation) {
    if (firstName && lastName) {
        return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    }

    return "";
}

export function reservationStatusMap(status: ReservationRaw["status"]) {
    const mapping = {
        accepted: {
            label: "Accepted",
            color: "success",
            // this is on web
            classNames: "text-success"
        },
        inquiry: {
            label: "Inquiry",
            color: "primary",
            classNames: ""
        },
        preapproved: {
            label: "Pre-Approved",
            color: "success",
            classNames: ""
        },
        denied: {
            label: "Denied",
            color: "error",
            classNames: "text-danger"
        },
        timedout: {
            label: "Expired Pre-Approval",
            color: "error",
            classNames: ""
        },
        pending: {
            label: "Booking Request",
            color: "primary",
            classNames: ""
        },
        cancelled: {
            label: "Cancelled",
            color: "error",
            classNames: "text-danger"
        },
        not_possible: {
            label: "Not possible",
            color: "blue",
            classNames: "text-danger"
        },
        pending_payment: {
            label: "Pending Payment",
            color: "primary",
            classNames: ""
        },
        checkpoint: {
            label: "Checkpoint",
            color: "primary",
            classNames: ""
        },
        awaiting_payment: {
            label: "Awaiting Payment",
            color: "primary",
            classNames: ""
        },
        timeout: {
            label: "Timeout",
            color: "error",
            classNames: "text-danger"
        },
        pending_verification: {
            label: "Pending Verification",
            color: "primary",
            classNames: ""
        },
        checkpoint_voided: {
            label: "Checkpoint Voided",
            color: "error",
            classNames: "text-danger"
        },
        deny: {
            label: "Deny",
            color: "error",
            classNames: "text-danger"
        },
        at_checkpoint: {
            label: "At Checkpoint",
            color: "primary",
            classNames: ""
        }
    } satisfies Record<
        ReservationRaw["status"],
        {
            label: string;
            color: ReservationStatusColors;
            classNames: string;
        }
    >;
    const result = mapping[status];
    return (
        result ?? {
            label: status,
            color: "danger",
            classNames: ""
        }
    );
}

export function getTaxFeePrice(taxesFees: BookingReservationObjTaxesFees) {
    let values = "";
    if (taxesFees.included) {
        values += "/Incl.";
    }
    if (taxesFees.perNight) {
        values += "/Ngt.";
    }
    if (taxesFees.perPerson) {
        values += "/Pers.";
    }
    return `${taxesFees.price}${values}`;
}

function takeFirstLetter(name: string) {
    const parts = name.split(" ");
    let firstLetter = "";
    if (parts.length >= 1) {
        firstLetter = parts[0].charAt(0);
    }
    if (parts.length >= 2) {
        firstLetter += parts[1].charAt(0);
    }
    return firstLetter.toUpperCase();
}
